/**
 * UserNotice model translations
 *
 * Translation keys follow the pattern: models.user-notice.{category}.{key}
 * Categories: fields, tabs, blocks, badges, actions, filters, columns, options, viewer
 *
 * vue-i18n resolves dots as nested paths, so nested field keys
 * like "user.full_name" must be structured as nested objects.
 *
 * FR locale uses typographic apostrophes (’) consistently.
 */
export default {
  en: {
    // Model names
    singular: "Notice",
    plural: "Notices",

    // Blocks
    blocks: {
      notice: "Notice Details",
      UserNotice: "Notice",
      schedule_manager: "Schedule Manager",
      schedule_history: "Schedule History",
      type_details: "Notice Details",
      documents: "Documents",
      formations: "Required Trainings"
    },

    // Tabs
    tabs: {
      main: "Notice",
      recipients: "Recipients",
      analytics: "Analytics",
      details: "Details",
      documents: "Documents",
      formations: "Trainings"
    },

    // Viewer (read-only) per-type copy
    viewer: {
      unknown_type: "No layout is defined for this notice type yet.",
      shared: {
        employee: "Employee",
        client: "Client",
        issued_by: "Issued by",
        reported_by: "Reported by",
        action_date: "Action date",
        incident_date: "Incident date",
        days_span: "Total days",
        days_count: "{count} day | {count} days",
        description: "Description",
        justification: "Justification"
      },
      disciplinary: {
        action_type: "Action"
      },
      incident: {
        severity: "Severity"
      },
      resignation: {
        notice_date: "Notice date"
      },
      equipment: {
        summary: "Summary",
        items: "Items",
        item_name: "Item",
        quantity: "Quantity",
        note: "Note"
      },
      hiring_kit: {
        deprecation_notice: "The Hiring Kit notice type is deprecated. New onboarding kits are sent through the Communication module."
      }
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      type: "Type",
      state: "State",
      user_id: "Employee",
      party_profile_id: "Client",
      details: "Details",
      date: "Date",
      start_date: "Start Date",
      end_date: "End Date",
      leave_type: "Leave Type",
      reason: "Reason",
      notice_period: "Notice Period",
      last_work_day: "Last Work Day",
      disciplinary_type: "Disciplinary Type",
      incident_severity: "Incident Severity",
      formations: "Required Trainings",
      created_at: "Created",
      updated_at: "Updated",
      full_name: "Employee",
      name: "Name",

      // Nested relation fields (used by lister card view + viewer)
      user: {
        full_name: "Employee"
      },
      client: {
        name: "Client"
      },
      creator: {
        full_name: "Issued by"
      },

      // Nested data fields
      app_data: {
        date: "Date",
        start_date: "Start Date",
        end_date: "End Date",
        disciplinary_type: "Disciplinary Type",
        incident_severity: "Incident Severity",
        leave_type: "Leave Type",
        reason: "Reason",
        notice_period: "Notice Period",
        last_work_day: "Last Work Day",
        equipment_summary: "Equipment Summary",
        items: "Items"
      }
    },

    // Badges (for select options and badge columns)
    badges: {
      // Notice types
      DISCIPLINARY: "Disciplinary",
      INCIDENT: "Incident",
      LEAVE: "Leave",
      VACATION: "Vacation",
      RESIGNATION: "Resignation",
      ABSENCE: "Absence",
      EQUIPMENT: "Equipment",
      HIRING_KIT: "Hiring kit",

      // States
      PENDING: "Pending",
      APPROVED: "Approved",
      REJECTED: "Rejected",

      // Disciplinary types
      FIRST_WRITTEN: "First Written Notice",
      SECOND_WRITTEN: "Second Written Notice",
      THIRD_WRITTEN: "Third Written Notice",
      DISMISSAL: "Dismissal Notice",
      WRITTEN: "Written Notice",
      VERBAL: "Verbal Notice",
      SUSPENSION: "Suspension",

      // Incident severities
      INCIDENT_SEVERITY: "Incident",
      MAJOR_INCIDENT: "Major Incident",
      MINOR_INCIDENT: "Minor Incident",
      COMPLAINT: "Complaint",
      MAJOR_COMPLAINT: "Major Complaint",

      // Leave types
      SICK_LEAVE: "Sick Leave",
      SUBSTITUTE_LEAVE: "Leave with Substitute"
    },

    // Filters
    filters: {
      type: "Type",
      state: "State"
    },

    // Columns
    columns: {
      employee: "Employee",
      client: "Client"
    },

    // Options (for select fields)
    // Pattern: options.{fieldKey}.{value}
    options: {
      type: {
        DISCIPLINARY: "Disciplinary",
        INCIDENT: "Incident",
        LEAVE: "Leave",
        VACATION: "Vacation",
        RESIGNATION: "Resignation",
        ABSENCE: "Absence",
        EQUIPMENT: "Equipment",
        HIRING_KIT: "Hiring kit"
      },
      state: {
        PENDING: "Pending",
        APPROVED: "Approved",
        REJECTED: "Rejected"
      },
      app_data: {
        disciplinary_type: {
          FIRST_WRITTEN: "First Written Notice",
          SECOND_WRITTEN: "Second Written Notice",
          THIRD_WRITTEN: "Third Written Notice",
          DISMISSAL: "Dismissal Notice",
          WRITTEN: "Written Notice",
          VERBAL: "Verbal Notice",
          SUSPENSION: "Suspension"
        },
        incident_severity: {
          INCIDENT: "Incident",
          MAJOR_INCIDENT: "Major Incident",
          MINOR_INCIDENT: "Minor Incident",
          COMPLAINT: "Complaint",
          MAJOR_COMPLAINT: "Major Complaint"
        },
        leave_type: {
          SICK_LEAVE: "Sick Leave",
          SUBSTITUTE_LEAVE: "Leave with Substitute"
        }
      }
    }
  },

  fr: {
    // Model names
    singular: "Avis",
    plural: "Avis",

    // Blocks
    blocks: {
      notice: "Détails de l’avis",
      UserNotice: "Avis",
      schedule_manager: "Gestionnaire de planification",
      schedule_history: "Historique de planification",
      type_details: "Détails de l’avis",
      documents: "Documents",
      formations: "Formations requises"
    },

    // Tabs
    tabs: {
      main: "Avis",
      recipients: "Destinataires",
      analytics: "Analyses",
      details: "Détails",
      documents: "Documents",
      formations: "Formations"
    },

    // Viewer (read-only) per-type copy
    viewer: {
      unknown_type: "Aucun affichage défini pour ce type d’avis.",
      shared: {
        employee: "Employé",
        client: "Client",
        issued_by: "Émis par",
        reported_by: "Signalé par",
        action_date: "Date de l’action",
        incident_date: "Date de l’incident",
        days_span: "Total de jours",
        days_count: "{count} jour | {count} jours",
        description: "Description",
        justification: "Justification"
      },
      disciplinary: {
        action_type: "Action"
      },
      incident: {
        severity: "Gravité"
      },
      resignation: {
        notice_date: "Date de l’avis"
      },
      equipment: {
        summary: "Résumé",
        items: "Articles",
        item_name: "Article",
        quantity: "Quantité",
        note: "Note"
      },
      hiring_kit: {
        deprecation_notice: "Le type d’avis « Trousse d’embauche » est obsolète. Les nouveaux dossiers d’accueil sont envoyés via le module Communication."
      }
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      type: "Type",
      state: "État",
      user_id: "Employé",
      party_profile_id: "Client",
      details: "Détails",
      date: "Date",
      start_date: "Date de début",
      end_date: "Date de fin",
      leave_type: "Type de congé",
      reason: "Raison",
      notice_period: "Période de préavis",
      last_work_day: "Dernier jour de travail",
      disciplinary_type: "Type disciplinaire",
      incident_severity: "Gravité de l’incident",
      formations: "Formations requises",
      created_at: "Créé le",
      updated_at: "Mis à jour",
      full_name: "Employé",
      name: "Nom",

      // Nested relation fields (used by lister card view + viewer)
      user: {
        full_name: "Employé"
      },
      client: {
        name: "Client"
      },
      creator: {
        full_name: "Émis par"
      },

      // Nested data fields
      app_data: {
        date: "Date",
        start_date: "Date de début",
        end_date: "Date de fin",
        disciplinary_type: "Type disciplinaire",
        incident_severity: "Gravité de l’incident",
        leave_type: "Type de congé",
        reason: "Raison",
        notice_period: "Période de préavis",
        last_work_day: "Dernier jour de travail",
        equipment_summary: "Résumé d’équipement",
        items: "Articles"
      }
    },

    // Badges (for select options and badge columns)
    badges: {
      // Notice types
      DISCIPLINARY: "Disciplinaire",
      INCIDENT: "Incident",
      LEAVE: "Congé",
      VACATION: "Vacances",
      RESIGNATION: "Démission",
      ABSENCE: "Absence",
      EQUIPMENT: "Équipement",
      HIRING_KIT: "Trousse d’embauche",

      // States
      PENDING: "En attente",
      APPROVED: "Approuvé",
      REJECTED: "Refusé",

      // Disciplinary types
      FIRST_WRITTEN: "Premier avis écrit",
      SECOND_WRITTEN: "Deuxième avis écrit",
      THIRD_WRITTEN: "Troisième avis écrit",
      DISMISSAL: "Avis de congédiement",
      WRITTEN: "Avis écrit",
      VERBAL: "Avis verbal",
      SUSPENSION: "Suspension",

      // Incident severities
      INCIDENT_SEVERITY: "Incident",
      MAJOR_INCIDENT: "Incident majeur",
      MINOR_INCIDENT: "Incident mineur",
      COMPLAINT: "Plainte",
      MAJOR_COMPLAINT: "Plainte majeure",

      // Leave types
      SICK_LEAVE: "Congé maladie",
      SUBSTITUTE_LEAVE: "Congé remplacement"
    },

    // Filters
    filters: {
      type: "Type",
      state: "État"
    },

    // Columns
    columns: {
      employee: "Employé",
      client: "Client"
    },

    // Options (for select fields)
    // Pattern: options.{fieldKey}.{value}
    options: {
      type: {
        DISCIPLINARY: "Disciplinaire",
        INCIDENT: "Incident",
        LEAVE: "Congé",
        VACATION: "Vacances",
        RESIGNATION: "Démission",
        ABSENCE: "Absence",
        EQUIPMENT: "Équipement",
        HIRING_KIT: "Trousse d’embauche"
      },
      state: {
        PENDING: "En attente",
        APPROVED: "Approuvé",
        REJECTED: "Refusé"
      },
      app_data: {
        disciplinary_type: {
          FIRST_WRITTEN: "Premier avis écrit",
          SECOND_WRITTEN: "Deuxième avis écrit",
          THIRD_WRITTEN: "Troisième avis écrit",
          DISMISSAL: "Avis de congédiement",
          WRITTEN: "Avis écrit",
          VERBAL: "Avis verbal",
          SUSPENSION: "Suspension"
        },
        incident_severity: {
          INCIDENT: "Incident",
          MAJOR_INCIDENT: "Incident majeur",
          MINOR_INCIDENT: "Incident mineur",
          COMPLAINT: "Plainte",
          MAJOR_COMPLAINT: "Plainte majeure"
        },
        leave_type: {
          SICK_LEAVE: "Congé maladie",
          SUBSTITUTE_LEAVE: "Congé remplacement"
        }
      }
    }
  }
};
